import { VITALL_CODE_MAP } from '@constants/fhir';
import { ALL_OBSERVATION_OPTIONS_MAP } from '@constants/option-types/vital-signs';
import * as ROUTES from '@constants/routes';

import { FhirResource, type BaseFhirFormValues } from '@models/fhir-resource';
import type { ImageType } from '@models/image-type';

import type { ObservationModelInput, ObservationModel } from 'generated/types';

import type {
  ObservationValues,
  ReducedObservationComponent,
} from './observation';
import { ObservationRecord } from './observation';

export enum TrackerType {
  Body = 'body',
  Fitness = 'fitness',
  Sleep = 'sleep',
  VitalSigns = 'vital-signs',
  HealthScore = 'health-score',
}

export const HEALTH_TRACKER_MAP: Record<
  TrackerType,
  { backRoute: string; friendly: string; icon?: ImageType }
> = {
  [TrackerType.VitalSigns]: {
    backRoute: ROUTES.VITAL_SIGNS,
    icon: 'vitalSigns',
    friendly: 'Vital Signs',
  },
  [TrackerType.Fitness]: {
    backRoute: ROUTES.FITNESS,
    icon: 'fitness',
    friendly: 'Daily Stats',
  },
  [TrackerType.Body]: {
    backRoute: ROUTES.BODY,
    icon: 'body',
    friendly: 'Body',
  },
  [TrackerType.Sleep]: {
    backRoute: ROUTES.SLEEP,
    icon: 'sleep',
    friendly: 'Sleep',
  },
  [TrackerType.HealthScore]: {
    backRoute: ROUTES.DASHBOARD,
    icon: 'logo',
    friendly: 'Health Score',
  },
};

export type VitalSignValues = Pick<
  ObservationValues,
  'clinicalStatus' | 'code' | 'selfReported' | 'source' | 'testDate'
>;

export type FormValues = BaseFhirFormValues &
  VitalSignValues & {
    components: ReducedObservationComponent[];
  };
export class VitalSignObservationRecord extends ObservationRecord {
  protected model: ObservationModel;

  public constructor(model: ObservationModel) {
    super(model);
    this.model = model;
  }

  /**
   * VITALL custom code that identifies the type of the observation
   */
  public get vitallCode(): string {
    const coding = this.model.code.coding?.find(({ system }) =>
      system?.startsWith('https://vitall.com')
    );
    return coding?.code ?? 'Unknown';
  }

  /**
   * All codes from different coding systems (e.g. Loinc, Snomed, etc.)
   */
  public override get code(): string {
    const coding = this.model.code.coding?.map(({ code }) => code).join(',');
    return coding ?? 'Unknown';
  }

  public static getComponentUnitOptions(code: string) {
    const vCode = code.includes(',') ? this.getVitallCode(code) : code;
    const components = ALL_OBSERVATION_OPTIONS_MAP[vCode].components;
    return (components ?? []).map((component) => {
      return component
        .reduce<string[]>((acc, curr) => {
          const { units } = curr;
          const joined = units.join('/');
          if (!acc.includes(joined)) {
            acc.push(joined);
          }
          return acc;
        }, [])
        .join('/');
    });
  }

  public override getComponents(
    selectedComponentIndex = 0
  ): ReducedObservationComponent[] {
    const components = super.getComponents();
    // Populate correct unit options within components
    for (let i = 0; i < components.length; i += 1) {
      const code = VitalSignObservationRecord.getVitallCode(this.code);
      const options = ALL_OBSERVATION_OPTIONS_MAP[code];
      let units: { k: string; v: string }[] = [];
      if (options.components?.length) {
        const component = options.components[selectedComponentIndex].find(
          ({ code }) => code === components[i].code
        );
        units = (component?.units ?? []).map((key) => ({
          k: key,
          v: key,
        }));
      } else if (options.units?.length) {
        units = options.units.map((key) => ({ k: key, v: key }));
      }
      components[i] = {
        ...components[i],
        units,
      };
    }
    return components;
  }

  public override setComponents(value: ReducedObservationComponent[]) {
    super.setComponents(value);
  }

  /**
   * Get an array of all observation measurements
   * @description Uses `valueQuantity` or `components` to populate an array of observation measurements
   * @param code Primary code for the observation
   * @param components Initial/Seed observation components
   * @returns An array of observation measurements
   */
  public static getMeasurementArray(
    code: string,
    components: ReducedObservationComponent[],
    selectedComponentIndex = 0
  ) {
    const vCode = code.includes(',') ? this.getVitallCode(code) : code;
    const options = ALL_OBSERVATION_OPTIONS_MAP[vCode];
    let rows: {
      code: string;
      display: string;
      value: number | null;
      unit: string | null;
      units: { k: string; v: string }[];
    }[] = [];
    rows = (
      options.components?.length
        ? options.components[selectedComponentIndex]
        : [
            {
              ...ALL_OBSERVATION_OPTIONS_MAP[vCode],
              code,
            },
          ]
    ).map((c) => {
      const match = components.find(({ code: _code }) => c.code === _code);
      const units = (c.units ?? []).map((unit) => ({ k: unit, v: unit }));
      const singleUnit = units.length === 1;

      return {
        code: c.code ?? code,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        display: c.display!,
        value: match?.value ?? null,
        unit: (singleUnit ? units[0].k : match?.unit) ?? null,
        units,
      };
    });
    return rows;
  }

  public override getModelInput(
    values?: VitalSignValues
  ): ObservationModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        if (key in this) {
          try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this[key] = values[key];
          } catch {
            // eslint-disable-next-line no-continue
            continue;
          }
        }
      }
    }
    if (values && 'components' in values) {
      this.setComponents((values as FormValues).components);
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    return cleaned;
  }

  public static getVitallCode(code: string): string {
    let vitallCode: string = '';
    if (!code.includes(',')) {
      vitallCode = code;
      return vitallCode;
    }
    for (const [key, value] of Object.entries(VITALL_CODE_MAP)) {
      const codes = code.split(',');
      const keys = key.split(',');
      for (const c of codes) {
        if (keys.includes(c)) {
          vitallCode = value;
        }
      }
    }
    return vitallCode;
  }
}
